import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import api from '../lib/api';
import {
  FileSpreadsheet, Plus, X, Send, Save, Users,
  CheckCircle2, AlertCircle, History,
} from 'lucide-react';
import { toast } from 'sonner';

function EmailChips({ label, items, onChange, placeholder, testId = 'emails' }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || items.includes(v)) { setDraft(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error('Email inválido');
      return;
    }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (e) => onChange(items.filter(x => x !== e));
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {label}
      </label>
      <div className="flex gap-1 mb-1.5">
        <Input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="text-sm"
          data-testid={`${testId}-input`}
        />
        <Button size="sm" type="button" onClick={add} variant="outline" data-testid={`${testId}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">Sin destinatarios.</span>}
        {items.map(e => (
          <Badge key={e} variant="secondary" className="gap-1 pr-1" data-testid={`${testId}-chip`}>
            {e}
            <button type="button" onClick={() => remove(e)} className="hover:bg-red-200 rounded-full p-0.5">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

// Iter 294.c (Carlos): informe Excel mensual de OTs en ejecución a Gerencia.
export default function InformeMensualOTsSection() {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, h] = await Promise.all([
        api.get('/informes/mensual-ots/config'),
        api.get('/informes/mensual-ots/preview'),
        api.get('/informes/mensual-ots/log?limit=10'),
      ]);
      setCfg(c.data);
      setPreview(p.data);
      setHistory(h.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando informe mensual OTs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/informes/mensual-ots/config', cfg);
      setCfg(res.data);
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if ((!cfg.to || cfg.to.length === 0) && (!cfg.grupos || cfg.grupos.length === 0)) {
      toast.error('Agrega al menos un destinatario o grupo antes de enviar');
      return;
    }
    if (!window.confirm(`Se enviará AHORA el ZIP con ${preview?.count || 0} informes Excel a los destinatarios configurados. ¿Continuar?`)) return;
    setSending(true);
    try {
      const res = await api.post('/informes/mensual-ots/enviar-ahora');
      const d = res.data;
      if (d.sent) {
        toast.success(`Informe enviado a ${d.to.length} destinatario(s) — ${d.ots_count} OTs`);
      } else {
        toast.info(`No enviado: ${d.reason || d.error || 'sin datos'}`);
      }
      api.get('/informes/mensual-ots/log?limit=10').then(r => setHistory(r.data?.items || []));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error enviando');
    } finally {
      setSending(false);
    }
  };

  if (loading || !cfg) {
    return <Card><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>;
  }

  const toggleGrupo = (key) => {
    const arr = new Set(cfg.grupos || []);
    if (arr.has(key)) arr.delete(key); else arr.add(key);
    setCfg({ ...cfg, grupos: Array.from(arr) });
  };
  const grupoActivo = (k) => (cfg.grupos || []).includes(k);
  const GRUPOS_DISPONIBLES = [
    { key: 'gerencia', label: 'Gerencia' },
    { key: 'corporativo', label: 'Corporativo' },
    { key: 'contabilidad', label: 'Contabilidad' },
  ];

  return (
    <Card data-testid="card-informe-mensual-ots">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" /> Informe Mensual — OTs en Ejecución
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Una vez al mes envía a Gerencia un <strong>ZIP</strong> con el informe Excel individual de cada OT en ejecución
              (estado EN EJECUCIÓN, N° ≥ 1000) + un Excel resumen consolidado.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{cfg.enabled ? 'Activo' : 'Pausado'}</span>
            <Switch
              checked={cfg.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
              data-testid="informe-mensual-switch-enabled"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1.5">Día del mes (1-28)</label>
            <Input
              type="number" min={1} max={28}
              value={cfg.dia}
              onChange={(e) => setCfg({ ...cfg, dia: parseInt(e.target.value, 10) || 1 })}
              data-testid="informe-mensual-input-dia"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">Hora de envío (24h, hora Chile)</label>
            <Input
              type="time"
              value={cfg.hora}
              onChange={(e) => setCfg({ ...cfg, hora: e.target.value })}
              data-testid="informe-mensual-input-hora"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">OTs que se incluirán hoy</label>
            <div className="text-sm font-bold text-emerald-700 pt-2" data-testid="informe-mensual-count-ots">
              {preview?.count ?? '—'} OTs en ejecución
            </div>
          </div>
        </div>

        {(preview?.ots?.length || 0) > 0 && (
          <div className="border rounded p-2 bg-slate-50 max-h-36 overflow-y-auto">
            <div className="flex flex-wrap gap-1.5">
              {preview.ots.map(o => (
                <span key={o.numero_ot} className="text-[11px] bg-white border rounded px-1.5 py-0.5" title={o.descripcion}>
                  <b className="font-mono">OT-{o.numero_ot}</b> · {(o.cliente_nombre || '').slice(0, 18)}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="pt-2 border-t">
          <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> Grupos de destinatarios
          </label>
          <div className="flex flex-wrap gap-2">
            {GRUPOS_DISPONIBLES.map(g => (
              <button
                key={g.key}
                type="button"
                onClick={() => toggleGrupo(g.key)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  grupoActivo(g.key)
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid={`informe-mensual-grupo-${g.key}`}>
                {g.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
          <EmailChips
            label="Destinatarios extra (Para)"
            items={cfg.to || []}
            onChange={(t) => setCfg({ ...cfg, to: t })}
            placeholder="email@chirinosteel.com"
            testId="informe-mensual-to"
          />
          <EmailChips
            label="Copia (CC)"
            items={cfg.cc || []}
            onChange={(c) => setCfg({ ...cfg, cc: c })}
            placeholder="copia@chirinosteel.com"
            testId="informe-mensual-cc"
          />
        </div>

        <div className="flex flex-wrap gap-2 pt-3 border-t">
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="informe-mensual-btn-save">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando…' : 'Guardar configuración'}
          </Button>
          <Button onClick={handleSendNow} disabled={sending} variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="informe-mensual-btn-send-now">
            <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Generando y enviando…' : 'Enviar ahora (test)'}
          </Button>
        </div>

        <div className="pt-2 border-t">
          <p className="text-xs font-medium flex items-center gap-1 mb-1.5"><History className="w-3.5 h-3.5" /> Historial de envíos</p>
          {history.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Aún no se han enviado informes.</p>
          ) : (
            <div className="divide-y border rounded">
              {history.map((h, idx) => (
                <div key={idx} className="px-3 py-2 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    {h.success ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
                    <span>{new Date(h.timestamp).toLocaleString('es-CL')}</span>
                    <Badge variant="outline" className="text-[10px]">{h.manual ? 'manual' : 'auto'}</Badge>
                    <span className="text-muted-foreground">periodo {h.periodo} → {(h.to || []).join(', ')}</span>
                  </div>
                  <div className="text-muted-foreground">
                    {h.ots_count} OT(s) {h.error && <span className="text-red-600 ml-2">{String(h.error).slice(0, 60)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
